import { buttonVariants } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { LoaderButton } from '@/components/ui/loader-button';
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useBulk from '@/hooks/useBulk';
import { fetchAllInstances } from '@/requests/api/fetch-all-instances';
import { useGetCount, useGetInfiniteList } from '@/requests/hooks';
import { tryCatch } from '@/utils/try-catch';
import { NotificationConfig, type Notification } from '@pigello/pigello-matrix';
import { Icons } from '@/components/icons';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Notification as NotificationComp } from './notification';
import { HIDDEN_NOTIFICATIONS_FILTER } from './utils';

const DEFAULT_AMOUNT = 25;

type TabState = 'all' | 'unread' | 'read';

export function NotificationSheetContent({
  userId,
  setSheetOpen,
}: {
  userId: string;
  setSheetOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const [tab, setTab] = useState<TabState>('all');
  const [isLoadingUnreadNotifications, setIsLoadingUnreadNotifications] =
    useState(false);

  const bulk = useBulk({ modelName: NotificationConfig.modelName });

  const { data: unreadNotificationCount } = useGetCount<Notification>({
    modelName: 'notification',
    filters: {
      read: {
        noop: false,
      },
      sent_to_organization_user: {
        noop: userId,
      },
      ...HIDDEN_NOTIFICATIONS_FILTER,
    },
  });
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useGetInfiniteList<Notification>({
      modelName: 'notification',
      queryParams: {
        order: ['-createdAt'],
        page: 1,
        pageSize: DEFAULT_AMOUNT,
        filters: {
          ...(tab === 'read' && {
            read: { noop: true },
          }),
          ...(tab === 'unread' && {
            read: { noop: false },
          }),
          sent_to_organization_user: {
            noop: userId,
          },
          ...HIDDEN_NOTIFICATIONS_FILTER,
        },
      },
    });

  const notifications = data?.pages.flatMap((d) => d.list);

  async function markAllAsRead() {
    setIsLoadingUnreadNotifications(true);
    const { data, error } = await tryCatch(async () => {
      return await fetchAllInstances<Notification>({
        modelName: 'notification',
        slim: true,
        filters: {
          read: {
            noop: false,
          },
          sent_to_organization_user: {
            noop: userId,
          },
          ...HIDDEN_NOTIFICATIONS_FILTER,
        },
      });
    });

    setIsLoadingUnreadNotifications(false);

    if (error) {
      toast.error('Kunde inte hämta notiser');
      return;
    }

    const unreadNotifications = data.list;

    const instancesToSave = unreadNotifications.map((n) => ({
      id: n.id,
      read: true,
    }));

    if (instancesToSave.length === 0) {
      toast.info('Du har inga olästa notifikationer');
      return;
    }

    const res = await bulk.save({ instances: instancesToSave });

    if (!res || res?.error) {
      toast.error('Misslyckades med att markera alla som lästa');
    } else {
      toast.success('Alla notiser har markerats som lästa');
    }

    bulk.reset();
  }

  return (
    <>
      <SheetHeader className='flex w-full flex-row items-center justify-between px-4 pt-4'>
        <SheetTitle>Systemnotiser</SheetTitle>
        <div className='flex items-center gap-2'>
          <LoaderButton
            isLoading={bulk?.loading || isLoadingUnreadNotifications}
            onClick={markAllAsRead}
            variant={'outline'}
            disabled={(unreadNotificationCount ?? 0) === 0}
          >
            <Icons.eye className='mr-2 size-4' />
            Markera alla som lästa
          </LoaderButton>
          <SheetClose
            className={buttonVariants({
              variant: 'ghost',
              size: 'icon-sm',
            })}
          >
            <Icons.close className='size-4' />
          </SheetClose>
        </div>
      </SheetHeader>
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as TabState)}
        defaultValue='all'
        className='flex flex-1 flex-col overflow-hidden'
      >
        <TabsList className='mx-4'>
          <TabsTrigger className='w-full' value='all'>
            Alla
          </TabsTrigger>
          <TabsTrigger className='w-full' value='read'>
            Lästa
          </TabsTrigger>
          <TabsTrigger className='w-full' value='unread'>
            Olästa
          </TabsTrigger>
        </TabsList>
        <div className='flex-1 overflow-x-hidden overflow-y-auto p-4'>
          {!isLoading && !isFetching && !notifications?.length && (
            <Empty className='h-full'>
              <EmptyHeader>
                <EmptyMedia variant='icon'>
                  <Icons.notificationOff />
                </EmptyMedia>
                <EmptyTitle>Inga notiser</EmptyTitle>
                <EmptyDescription>
                  Du har inga notifikationer. Passa på att ta en rast och sträck
                  lite på benen! :)
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
          {notifications?.map((notification) => (
            <NotificationComp
              key={notification.id}
              notification={notification}
              setSheetOpen={setSheetOpen}
            />
          ))}
          {isLoading && (
            <div className='flex flex-col gap-2'>
              {Array.from({ length: 6 }).map((_, i) => (
                <NotificationSkeleton key={i} />
              ))}
            </div>
          )}
          {!isLoading && hasNextPage && (
            <div className='mt-2 flex w-full justify-center'>
              <LoaderButton
                isLoading={isFetching}
                variant={'outline'}
                onClick={() => void fetchNextPage()}
              >
                Ladda fler notiser
              </LoaderButton>
            </div>
          )}
        </div>
      </Tabs>
    </>
  );
}

function NotificationSkeleton() {
  return (
    <Skeleton className='flex w-full items-start justify-between rounded-md bg-muted/40 p-2'>
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-4 w-56' />
        <Skeleton className='h-3 w-40' />
        <Skeleton className='h-2 w-36' />
      </div>
    </Skeleton>
  );
}
